import type {
  DesktopSales,
  ItemSales,
  ItemSalesPrintOutcome,
  LoggedSale,
  OutletItemSales,
  PaymentSales,
  RecentSales,
} from '@repo/desktop-bridge';
import { PARK_TIME_ZONE } from '@repo/desktop-bridge';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { IconAlertTriangle, IconPrinter } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { formatPaise } from '@/lib/money';

import { OutletPayments } from './OutletPayments';
import { paymentModeLabel } from './payment-modes';
import { TillActions, tillPlate, tillScroll } from './TillFrame';

/**
 * What this till has taken today — a list to read, and nothing to press.
 *
 * **The till sells and does not correct (#294).** This screen was built so a
 * sale could be refunded or its receipt reprinted at the counter, and both
 * controls are gone. The park watched what the reprint was used for: an
 * operator takes the money, never records the sale, and reprints an earlier
 * receipt to hand over — paper that looks right to a visitor for a sale the
 * day's takings never see. The duplicate mark and the audit row made that
 * detectable, not prevented, and narrowing the reprint to the latest sale
 * narrows the window and closes nothing. A refund is the same lever on the
 * drawer. So a correction is a manager's, in the back office, which looks the
 * sale up by the receipt number on this list — and the screen says so, in one
 * line, because this is where a cashier asked for a refund goes looking.
 *
 * **Do not put either control back here** without reading #294. The rows are
 * still the right thing to name a sale by; what changed is who acts on one.
 *
 * ## What it is, and what it is not
 *
 * It is **this device's own log**, read with no round trip, and it works with
 * the line down like everything else at this counter. It is not the day's
 * takings for the outlet — a second till at the same counter keeps its own and
 * neither can see the other — and it is not the business day, which is the
 * server's and which no route publishes. The heading says "today at this till"
 * for exactly that reason.
 *
 * ## Every outlet the till holds, and whose shift (#290)
 *
 * It is reached from both counters, and it reads every registration's log: a
 * basket across outlets is a sale in each outlet's own journal (#284), and a
 * list of one would hide the other half from both counters. Where any sale is
 * another outlet's, every row names its outlet. A till that sells for one
 * outlet names none, and looks as it always did.
 *
 * It **opens on the cashier's own shift** — what have I done — with the whole
 * till's day one tap away. Both questions are real, and the second is how a
 * visitor coming back after a shift change is found: their receipt number is on
 * the till's list whoever rang it up.
 *
 * ## A refund this till queued still shows
 *
 * A sale this till queued a refund for before #294 still carries
 * `refundQueuedAt`, and the row says so — the device's journal records what it
 * recorded, and the refund is a fact about the sale. A refund made in the back
 * office is invisible here, as it always was: the device does not own the
 * answer.
 */

/**
 * The time of day, and nothing else.
 *
 * Every row on this screen happened today, so `formatInstant`'s date would be
 * the same eleven times down a list somebody is scanning for a receipt number.
 * In Munnar's zone, never the machine's: the tills ship on US Pacific (#331).
 */
const TIME = new Intl.DateTimeFormat('en-IN', { timeZone: PARK_TIME_ZONE, timeStyle: 'short' });

/**
 * `new Date(iso)` on a value this device wrote down. It reads no clock —
 * `lib/clock.ts` governs that, and nothing here asks what time it is now.
 */
const formatTime = (iso: string): string => TIME.format(new Date(iso));

/** Which of the screen's two questions is on it. */
type Whose = 'mine' | 'till';

/** The day as receipts, item by item (#314), or by payment mode (#323). */
type Shape = 'receipts' | 'items' | 'payments';

/** What a press of Print summary came to, in words a cashier acts on. */
const PRINT_NOTES: Record<
  Extract<ItemSalesPrintOutcome, { printed: false }>['reason'] | 'PRINTED',
  string
> = {
  PRINTED: 'Printed. Take the slip from the printer.',
  PRINTER_UNCONFIGURED:
    'No printer is set up on this till, so nothing printed. Set one up under Printer, then print again.',
  NOTHING_TO_PRINT: 'Nothing has been sold here yet, so there is nothing to print.',
  PRINT_FAILED:
    'The printer did not take the summary. Check it is on and has paper, then print again.',
};

export function TodaysSales({
  sales,
  onBack,
}: {
  /** The bridge slice answering `recent()`. See `desktop/bridge.ts`. */
  sales: DesktopSales;
  /** Back to the counter. */
  onBack: () => void;
}) {
  /** `'unknown'` until the device answers, `null` when it could not. */
  const [answer, setAnswer] = useState<RecentSales | null | 'unknown'>('unknown');

  /** The cashier's own shift first; the whole till's day is the other choice. */
  const [whose, setWhose] = useState<Whose>('mine');

  const [shape, setShape] = useState<Shape>('receipts');
  /** The day item by item: `'unknown'` until the device answers, `null` when it could not. */
  const [items, setItems] = useState<ItemSales | null | 'unknown'>('unknown');
  /** The day by payment mode, the same way. */
  const [payments, setPayments] = useState<PaymentSales | null | 'unknown'>('unknown');
  const [printing, setPrinting] = useState(false);
  const [printNote, setPrintNote] = useState<string | null>(null);

  useEffect(() => {
    sales
      .recent()
      .then(setAnswer)
      .catch((error: unknown) => {
        console.error('till: the day’s sales could not be read', error);
        setAnswer(null);
      });
  }, [sales]);

  // Read when the cashier first asks for it, rather than on every visit.
  useEffect(() => {
    if (shape !== 'items') return;

    sales
      .itemSales()
      .then(setItems)
      .catch((error: unknown) => {
        console.error('till: the day’s sales by item could not be read', error);
        setItems(null);
      });
  }, [sales, shape]);

  useEffect(() => {
    if (shape !== 'payments') return;

    sales
      .paymentSales()
      .then(setPayments)
      .catch((error: unknown) => {
        console.error('till: the day’s sales by payment mode could not be read', error);
        setPayments(null);
      });
  }, [sales, shape]);

  const day = answer === 'unknown' || answer === null ? null : answer;
  /** `null` with nobody signed in: then there is no shift to narrow to, only the day. */
  const mine = day?.mine ?? null;
  const onMine = whose === 'mine' && mine !== null;
  const list = onMine ? mine : day;

  const shown = list?.sales ?? [];
  const hidden = list === null ? 0 : list.total - list.sales.length;

  /**
   * Whether rows name their outlet: where any sale today is another outlet's.
   * Judged on the whole day, so it does not change under the cashier's thumb
   * when they switch between the two lists.
   */
  const named = day?.sales.some((sale) => sale.registration !== day.here) ?? false;

  const report = items === 'unknown' || items === null ? null : items;
  const outlets = (onMine ? report?.mine?.outlets : report?.outlets) ?? [];

  const paid = payments === 'unknown' || payments === null ? null : payments;
  const paidOutlets = (onMine ? paid?.mine?.outlets : paid?.outlets) ?? [];

  const print = () => {
    setPrinting(true);
    setPrintNote(null);
    const view = onMine ? 'mine' : 'till';

    (shape === 'payments' ? sales.printPaymentSales(view) : sales.printItemSales(view))
      .then((outcome) => {
        setPrintNote(PRINT_NOTES[outcome.printed ? 'PRINTED' : outcome.reason]);
      })
      .catch((error: unknown) => {
        console.error('till: the summary could not be printed', error);
        setPrintNote(PRINT_NOTES.PRINT_FAILED);
      })
      .finally(() => {
        setPrinting(false);
      });
  };

  return (
    // The head and the way back do not move; the list takes what is left and
    // scrolls inside itself. `min-h-0` is what lets it — without it a long day
    // pushes the whole screen past the fold, which `TillFrame` then clips.
    <div className="mx-auto flex h-full w-full max-w-[52rem] flex-col gap-5">
      {/* **The title band is gone — Move 1 of issue #206.** The way back is
          chrome rather than part of the day, so it goes beside the mark with the
          rest of the till's chrome; the name goes with it, because unlike the
          counter this screen is pushed *over* the counter and a list of receipt
          numbers with no label on it is a list of receipt numbers.

          The sentence under the old title went: "Pick one to refund it or
          reprint its receipt" was learned once and then read every time, and
          this screen's whole problem is vertical room. One line came back in
          #294, below the shift choice, and it earns its row differently — it
          is the answer to a question a visitor asks, which a cashier has to
          give without a control to press. */}
      <TillActions label={'Today\u2019s sales'}>
        <Button variant="outline" size="touch" onClick={onBack}>
          Back to the counter
        </Button>
      </TillActions>

      {/* The two questions a cashier has of this screen (#290). Absent with
          nobody's shift to narrow to, where the day is the only answer.
          Composed from buttons as `Till.tsx`'s counter choice is: the registry
          has no toggle group, and adding one means a `shadcn add` that has
          overwritten the button's touch sizes three times. */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3">
        {mine !== null && (
          <div role="radiogroup" aria-label="Whose sales" className="flex shrink-0 gap-2">
            {(
              [
                { value: 'mine', label: 'Your shift' },
                { value: 'till', label: 'Today at this till' },
              ] as const
            ).map(({ value, label }) => (
              <Button
                key={value}
                role="radio"
                aria-checked={whose === value}
                // Neutral either way, as on the counter choice: the one accent on
                // a till screen belongs to an action that takes money.
                variant={whose === value ? 'secondary' : 'outline'}
                size="touch"
                onClick={() => {
                  setWhose(value);
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        )}

        {/* The same day as receipts or item by item (#314) — built as the shift
          choice beside it is, and for the same reason. */}
        <div role="radiogroup" aria-label="Show" className="flex shrink-0 gap-2">
          {(
            [
              { value: 'receipts', label: 'Receipts' },
              { value: 'items', label: 'By item' },
              { value: 'payments', label: 'By payment' },
            ] as const
          ).map(({ value, label }) => (
            <Button
              key={value}
              role="radio"
              aria-checked={shape === value}
              variant={shape === value ? 'secondary' : 'outline'}
              size="touch"
              onClick={() => {
                setShape(value);
                setPrintNote(null);
              }}
            >
              {label}
            </Button>
          ))}
        </div>

        {shape !== 'receipts' && (
          <Button
            variant="outline"
            size="touch"
            className="ms-auto"
            disabled={printing}
            onClick={print}
          >
            <IconPrinter />
            Print summary
          </Button>
        )}
      </div>

      {printNote !== null && (
        <p role="status" className="shrink-0 text-base text-foreground">
          {printNote}
        </p>
      )}

      {/* Where a correction is made, said once, where a cashier asked for one
          goes looking (#294) — and not on a selling screen. A sentence and not
          a link: the back office is not reachable from a till shell. */}
      <p className="shrink-0 text-base text-muted-foreground">
        A refund or a reprint is made in the back office, where a manager finds the sale by its
        receipt number.
      </p>

      <div className={`min-h-0 flex-1 ${tillScroll}`}>
        {shape === 'payments' ? (
          payments === 'unknown' ? (
            <ListSkeleton />
          ) : payments === null ? (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertTitle>This till could not read today&rsquo;s sales by payment</AlertTitle>
              <AlertDescription>
                The sales themselves are fine — they are recorded and they will reach the office.
                What failed is this summary. Go back to the counter and keep selling, and report
                this.
              </AlertDescription>
            </Alert>
          ) : paidOutlets.length === 0 ? (
            <p className="py-6 text-base text-muted-foreground">
              Nothing has been sold {onMine ? 'this shift' : 'at this till today'} yet. Each payment
              is counted here as it is taken, admission tickets included.
            </p>
          ) : (
            <div className="space-y-8">
              {paidOutlets.map((each) => (
                <OutletPayments
                  key={each.registration}
                  sales={each}
                  named={paidOutlets.length > 1}
                />
              ))}
            </div>
          )
        ) : shape === 'items' ? (
          items === 'unknown' ? (
            <ListSkeleton />
          ) : items === null ? (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertTitle>This till could not read today&rsquo;s sales by item</AlertTitle>
              <AlertDescription>
                The sales themselves are fine — they are recorded and they will reach the office.
                What failed is this summary. Go back to the counter and keep selling, and report
                this.
              </AlertDescription>
            </Alert>
          ) : outlets.length === 0 ? (
            <p className="py-6 text-base text-muted-foreground">
              Nothing has been sold {onMine ? 'this shift' : 'at this till today'} yet. Items appear
              here as they are rung up, admission tickets included.
            </p>
          ) : (
            <div className="space-y-8">
              {outlets.map((each) => (
                <OutletItems key={each.registration} sales={each} named={outlets.length > 1} />
              ))}
            </div>
          )
        ) : answer === 'unknown' ? (
          <ListSkeleton />
        ) : answer === null ? (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertTitle>This till could not read today&rsquo;s sales</AlertTitle>
            <AlertDescription>
              The sales themselves are fine — they are recorded and they will reach the office. What
              failed is this list. Go back to the counter and keep selling, and report this.
            </AlertDescription>
          </Alert>
        ) : shown.length === 0 ? (
          // Says what would be here and how the first one arrives, rather than
          // "No results" (`docs/ui-conventions.md`). It is also the honest
          // answer for a till that has just been switched on this morning.
          onMine ? (
            <p className="py-6 text-base text-muted-foreground">
              You have sold nothing this shift yet. Your sales appear here as you ring them up;
              everything this till has sold today is under Today at this till.
            </p>
          ) : (
            <p className="py-6 text-base text-muted-foreground">
              Nothing has been sold at this till today. Sales appear here as they are rung up, and
              stay for the rest of the day.
            </p>
          )
        ) : (
          <ul className="space-y-3">
            {shown.map((sale) => (
              <SaleRow key={sale.id} sale={sale} named={named} />
            ))}
          </ul>
        )}

        {/* **No silent cap.** The list is bounded so a busy Sunday does not ship
            four hundred rows across IPC to draw twelve, and a bounded list that
            does not say so reads as the whole day. */}
        {shape === 'receipts' && hidden > 0 && (
          <p className="py-4 text-sm text-muted-foreground">
            Showing the {shown.length} most recent of {shown.length + hidden} sales{' '}
            {onMine ? 'this shift' : 'today'}. The back office has every one of them.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * One sale, identified the way it is identified at a counter.
 *
 * The number is the largest thing on the row and set in mono with tabular
 * figures: it is what the customer is holding, what gets read aloud, and what a
 * cashier scans down the list for. The amount is beside it because the two
 * together are how somebody confirms they have found the right sale before
 * sending a visitor to a manager with it.
 */
function SaleRow({
  sale,
  named,
}: {
  sale: LoggedSale;
  /** Whether to say which outlet it was sold at — only where the list spans more than one. */
  named: boolean;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-2xl border border-border bg-card px-5 py-4">
      <div className="min-w-0">
        <p className="font-mono text-2xl leading-none text-foreground numeric">{sale.number}</p>
        <p className={`${tillPlate} mt-2`}>
          {/* Where first: it is how the visitor names what they bought, and it
              is where the money goes back from (#290). Who sold it is the
              staff code, from the shift it was rung under — a till is shared. */}
          {named && sale.outlet !== null && `${sale.outlet} · `}
          {formatTime(sale.occurredAt)} · {paymentModeLabel(sale.paymentMode)} · {sale.staffCode}
          {/* Carried through from the sale itself. A receipt that never came out
              is not a failed sale — the money was taken and the record is on its
              way — but it is worth saying here, because it is the row somebody
              will be asked about. */}
          {!sale.printed && ' · no receipt printed'}
        </p>
      </div>

      <div className="flex items-center gap-5">
        <span className="font-mono text-2xl leading-none text-foreground numeric">
          {formatPaise(sale.totalPaise)}
        </span>

        {sale.refundQueuedAt !== null && (
          // Stated as a queued write and not as a settled one, which is what it
          // is: this till queued it before #294, and the office agreeing is a
          // separate event this device may not have heard about yet.
          <span className="text-sm text-muted-foreground">Refunded on this till</span>
        )}
      </div>
    </li>
  );
}

/**
 * One outlet's day, item by item (#314): a row per item with its HSN under the
 * name, then the old machine's daily summary — bills, quantity, tax, amount.
 *
 * Every figure is the main process's, in integer paise; nothing is added up
 * here. Admission tickets are counted, which the receipts list leaves out, and
 * a sale journalled before the till kept its lines is one row, so the amount
 * is still what the till took.
 */
function OutletItems({
  sales,
  named,
}: {
  sales: OutletItemSales;
  /** Whether to head it with its outlet — only where more than one sold today. */
  named: boolean;
}) {
  const { unitemised } = sales;

  return (
    <section className="space-y-4">
      {named && <h2 className={tillPlate}>{sales.outlet ?? sales.terminal}</h2>}

      <table className="w-full text-base">
        <thead>
          <tr className={tillPlate}>
            <th className="pb-2 text-left font-semibold">Item</th>
            <th className="pb-2 text-right font-semibold">Qty</th>
            <th className="pb-2 text-right font-semibold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {sales.lines.map((line) => (
            <tr
              key={`${line.catalogueItemId}:${line.itemName}:${line.hsnCode ?? ''}`}
              className="border-t border-border"
            >
              <td className="py-3 pe-4">
                <p className="text-foreground">{line.itemName}</p>
                {line.hsnCode !== null && (
                  <p className="text-sm text-muted-foreground numeric">HSN {line.hsnCode}</p>
                )}
              </td>
              <td className="py-3 text-right align-top numeric">{line.quantity}</td>
              <td className="py-3 text-right align-top numeric">{formatPaise(line.amountPaise)}</td>
            </tr>
          ))}

          {/* The known limit, said where it bites: the till kept no lines
              before this, so those sales are counted and not broken down. */}
          {unitemised.bills > 0 && (
            <tr className="border-t border-border">
              <td className="py-3 pe-4">
                <p className="text-foreground">Before item detail was kept</p>
                <p className="text-sm text-muted-foreground">
                  <span className="numeric">{unitemised.bills}</span>{' '}
                  {unitemised.bills === 1 ? 'bill' : 'bills'}, counted but not broken down by item
                </p>
              </td>
              <td />
              <td className="py-3 text-right align-top numeric">
                {formatPaise(unitemised.amountPaise)}
              </td>
            </tr>
          )}

          {sales.roundOffPaise !== 0 && (
            <tr className="border-t border-border text-muted-foreground">
              <td className="py-3 pe-4">Round off</td>
              <td />
              <td className="py-3 text-right numeric">{formatPaise(sales.roundOffPaise)}</td>
            </tr>
          )}
        </tbody>
      </table>

      <ul
        aria-label="Totals"
        className="grid grid-cols-2 gap-3 rounded-2xl border border-border bg-card px-5 py-4 sm:grid-cols-4"
      >
        {(
          [
            ['Bills', String(sales.bills)],
            ['Quantity', String(sales.quantity)],
            ['Tax', formatPaise(sales.taxPaise)],
            ['Amount', formatPaise(sales.amountPaise)],
          ] as const
        ).map(([label, value]) => (
          <li key={label} className="flex flex-col gap-1">
            <span className={tillPlate}>{label}</span>
            <span className="font-mono text-2xl leading-none text-foreground numeric">{value}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A skeleton in the shape of the rows, so nothing shifts when they land. */
function ListSkeleton() {
  return (
    <ul className="space-y-3" aria-hidden data-testid="till-recent-sales-skeleton">
      {[0, 1, 2, 3].map((slot) => (
        <li key={slot} className="h-touch animate-pulse rounded-2xl bg-muted" />
      ))}
    </ul>
  );
}
